import React, { useCallback, useEffect, useRef, useState } from 'react';
import { hydrateImages } from '../../components/docTools.js';
import { hydrateBlocks } from '../../components/docBlocks.js';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { api } from '../../api.js';
import { useI18n } from '../../i18n/index.jsx';
import { Alert } from '../../components/ui.jsx';
import { StatusBadge, DOC_KINDS, fmtDate, Modal, labelOf } from './ws.jsx';
import { MailboxBanner } from './CustomerPage.jsx';
import { DocsNav, PdfFrame, pdfBlobUrl } from './DocLibrary.jsx';
import DocWizard, { totalsOf, money, emptyWarning } from './DocWizard.jsx';
import TutorialSlot from '../../tutorials/TutorialSlot.jsx';

const STEPS = ['draft', 'pending_approval', 'sent', 'viewed', 'accepted'];
const STATUSES = ['draft', 'pending_approval', 'sent', 'viewed', 'replied', 'accepted', 'declined', 'expired'];

async function openPdf(id, errorText, setError) {
  // Opened first (same click), then filled: phones block a window opened later.
  const w = window.open('', '_blank');
  try { const url = await pdfBlobUrl(`/workspace/documents/${id}/pdf`); if (w) w.location.href = url; else window.location.href = url; }
  catch (e) { w?.close(); setError(errorText(e)); }
}

// "Envoyés / Sent": search and filters (customer, type, status, dates);
// open, PDF, re-send, duplicate. Documents waiting for my approval first.
export function DocumentsList({ base = '/account/workspace' }) {
  const { t, lang, errorText } = useI18n();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [f, setF] = useState({ q: '', kind: '', status: '', crmId: '', from: '', to: '' });
  const [error, setError] = useState('');
  useEffect(() => {
    const tm = setTimeout(() => {
      const qs = Object.entries(f).filter(([, v]) => v).map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join('&');
      api(`/workspace/documents${qs ? `?${qs}` : ''}`).then(setData).catch((e) => setError(errorText(e)));
    }, f.q ? 250 : 0);
    return () => clearTimeout(tm);
  }, [f, errorText]);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const pending = data?.canApprove ? data.documents.filter((d) => d.status === 'pending_approval') : [];
  async function duplicate(d) {
    try { const n = await api(`/workspace/documents/${d.id}/duplicate`, { method: 'POST' }); navigate(`${base}/documents/${n.id}/edit`); } catch (e) { setError(errorText(e)); }
  }
  return (
    <div className="ws-page">
      <DocsNav base={base} />
      <div className="acc-head"><TutorialSlot place="ws.docs.sent" />
        <div><h1>{t('ws.docs.title')}</h1><p className="muted">{t('ws.docs.lead')}</p></div>
        {data?.canCreate && <div className="acc-head-actions"><Link className="btn btn-gold" to={`${base}/documents/new`}>+ {t('ws.docs.tab.new')}</Link></div>}
      </div>
      <Alert>{error}</Alert>
      {pending.length > 0 && !f.status && <p className="callout callout-warning">{t('ws.docs.toApprove', { n: pending.length })}</p>}
      <div className="acc-toolbar ws-doc-filters">
        <input type="search" value={f.q} onChange={(e) => set('q', e.target.value)} placeholder={t('ws.docs.search')} aria-label={t('ws.docs.search')} />
        <select value={f.crmId} onChange={(e) => set('crmId', e.target.value)} aria-label={t('ws.cust.company')}><option value="">{t('ws.docs.allCustomers')}</option>{(data?.customers || []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select>
        <select value={f.kind} onChange={(e) => set('kind', e.target.value)} aria-label={t('ws.docs.kind')}><option value="">{t('ws.docs.allKinds')}</option>{DOC_KINDS.map((k) => <option key={k} value={k}>{t(`ws.kind.${k}`)}</option>)}</select>
        <select value={f.status} onChange={(e) => set('status', e.target.value)} aria-label={t('ws.cust.status')}><option value="">{t('ws.docs.allStatuses')}</option>{STATUSES.map((s) => <option key={s} value={s}>{labelOf(t, 'ws.doc', s)}</option>)}</select>
        <label className="ws-date"><span className="small muted">{t('ws.docs.from')}</span><input type="date" value={f.from} onChange={(e) => set('from', e.target.value)} /></label>
        <label className="ws-date"><span className="small muted">{t('ws.docs.to')}</span><input type="date" value={f.to} onChange={(e) => set('to', e.target.value)} /></label>
      </div>
      {!data ? <p className="muted">{t('common.loading')}</p> : (
        <div className="ws-table-wrap">
          <table className="ws-table ws-table-cards">
            <thead><tr><th>{t('ws.docs.ref')}</th><th>{t('ws.docs.kind')}</th><th>{t('ws.cust.company')}</th><th>{t('ws.cust.status')}</th><th>{t('ws.docs.by')}</th><th>{t('ws.docs.date')}</th><th>{t('ws.docs.actions')}</th></tr></thead>
            <tbody>
              {[...pending, ...data.documents.filter((d) => !pending.includes(d))].map((d) => (
                <tr key={d.id}>
                  <td className="ws-td-main"><Link to={`${base}/documents/${d.id}`}>{d.ref}</Link>{d.version > 1 && <span className="ws-badge">v{d.version}</span>}</td>
                  <td data-label={t('ws.docs.kind')}>{t(`ws.kind.${d.kind}`)} · {d.lang.toUpperCase()}</td>
                  <td data-label={t('ws.cust.company')}>{d.customer}</td>
                  <td data-label={t('ws.cust.status')}><StatusBadge kind="doc" value={d.status} />{d.replyCount > 0 && <span className="ws-badge ws-doc-reply-badge" title={t('ws.docs.repliesN', { n: d.replyCount })}>💬 {d.replyCount}</span>}</td>
                  <td data-label={t('ws.docs.by')} className="small">{d.createdBy}</td>
                  <td data-label={t('ws.docs.date')} className="small">{fmtDate(d.respondedAt || d.viewedAt || d.sentAt || d.createdAt, lang)}</td>
                  <td data-label={t('ws.docs.actions')} className="ws-row-actions">
                    <button type="button" className="btn btn-link btn-sm" onClick={() => openPdf(d.id, errorText, setError)}>PDF</button>
                    {['sent', 'viewed', 'replied', 'expired'].includes(d.status) && <Link className="btn btn-link btn-sm" to={`${base}/documents/${d.id}?send=1`}>{t('ws.docs.resend')}</Link>}
                    {d.status === 'draft' && <Link className="btn btn-link btn-sm" to={`${base}/documents/${d.id}/edit`}>{t('ws.docs.edit')}</Link>}
                    {data.canCreate && <button type="button" className="btn btn-link btn-sm" onClick={() => duplicate(d)}>{t('ws.docs.duplicate')}</button>}
                  </td>
                </tr>
              ))}
              {!data.documents.length && <tr><td colSpan={7} className="muted">{t('ws.docs.empty')}</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// The new-document screen and an existing draft's editor are the wizard.
export function DocumentNew({ base = '/account/workspace' }) { return <DocWizard base={base} />; }
export function DocumentEdit({ base = '/account/workspace' }) { return <DocWizard base={base} />; }

// The document's text as on the PDF (items table where {items_table} is).
function ItemsPreview({ items, lang }) {
  const { t } = useI18n();
  const tot = totalsOf(items);
  if (!items?.rows?.length) return null;
  return (
    <table className="ws-doc-items">
      <thead><tr><th>{t('ws.dw.it.product')}</th><th>{t('ws.dw.it.qty')}</th><th>{t('ws.dw.it.unit')}</th>{tot.priced && <><th>{t('ws.dw.it.unitPrice')}</th><th>{t('ws.dw.it.total')}</th></>}</tr></thead>
      <tbody>
        {items.rows.map((r, i) => (
          <tr key={i}><td><strong>{r.product}</strong>{r.specs && <><br /><span className="small muted">{r.specs}</span></>}</td><td>{r.qty}</td><td>{r.unit}</td>
            {tot.priced && <><td>{r.unitPrice ? money(Number(String(r.unitPrice).replace(',', '.')), lang) : ''}</td><td>{tot.lines[i] === null ? '' : money(tot.lines[i], lang)}</td></>}</tr>
        ))}
      </tbody>
      {tot.priced && (
        <tfoot>
          <tr><td colSpan={4}>{t('ws.dw.subtotal')}</td><td>{money(tot.subtotal, lang)} {items.currency}</td></tr>
          {tot.tax > 0 && <tr><td colSpan={4}>{items.taxLabel || t('ws.dw.tax')} ({items.taxRate} %)</td><td>{money(tot.tax, lang)} {items.currency}</td></tr>}
          <tr className="is-total"><td colSpan={4}>{t('ws.dw.total')}</td><td>{money(tot.total, lang)} {items.currency}</td></tr>
        </tfoot>
      )}
    </table>
  );
}
// Server-sanitized rich text with escaped values; its private pictures shown through the API.
function DocHtml({ html }) {
  const ref = useRef(null);
  useEffect(() => { hydrateBlocks(ref.current); hydrateImages(ref.current); }, [html]);
  return <div ref={ref} dangerouslySetInnerHTML={{ __html: html }} />;
}
function Preview({ html, items, lang }) {
  const parts = String(html || '').split(/\{items_table\}/);
  return (
    <div className="ws-doc-preview rt-content rich-text">
      {parts.map((p, i) => (
        <React.Fragment key={i}>
          <DocHtml html={p} />
          {i < parts.length - 1 && <ItemsPreview items={items} lang={lang} />}
        </React.Fragment>
      ))}
      {parts.length === 1 && items?.rows?.length > 0 && <ItemsPreview items={items} lang={lang} />}
    </div>
  );
}

// "Envoyer par courriel": to, cc, subject, message, PDF attached; and / or the chat.
function SendDialog({ d, onClose, onSent }) {
  const { t, errorText } = useI18n();
  const e = d.emailDefaults;
  const [byEmail, setByEmail] = useState(true);
  const [f, setF] = useState({ to: e.to, cc: e.cc, subject: e.subject, message: e.message, link: true });
  const [chat, setChat] = useState(!!d.conversationId);
  const [validUntil, setValidUntil] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const warning = emptyWarning(d, t);
  async function send() {
    // Empty fields used in the text, or an empty items table: asked before sending.
    if (warning && !window.confirm(`${warning}\n\n${t('ws.send.sendAnyway')}`)) return;
    setBusy(true); setError('');
    try {
      const r = await api(`/workspace/documents/${d.id}/send`, { method: 'POST', body: { email: byEmail ? f : false, chat, ...(d.status === 'expired' ? { validUntil } : {}) } });
      onSent(r);
    } catch (err) { setError(errorText(err)); setBusy(false); }
  }
  return (
    <Modal title={`${t('ws.send.title')} — ${d.ref}`} wide onClose={onClose}>
      {warning && <p className="callout callout-warning small" role="alert">⚠ {warning}</p>}
      {d.status === 'expired' && (
        <label className="field"><span className="field-label">{t('ws.send.newValidity')}</span>
          <input type="date" value={validUntil} onChange={(x) => setValidUntil(x.target.value)} />
          <span className="field-hint">{t('ws.send.newValidityHint')}</span>
        </label>
      )}
      <label className="field-checkbox"><input type="checkbox" checked={byEmail} onChange={(x) => setByEmail(x.target.checked)} /><span>{t('ws.send.byEmail')}</span></label>
      {byEmail && (
        <div className="ws-form">
          <div className="ws-full"><MailboxBanner mailbox={d.mailbox} /></div>
          <label className="field ws-full"><span className="field-label">{t('ws.send.to')}</span><input type="email" multiple value={f.to} onChange={(x) => set('to', x.target.value)} />{!d.customerEmailKnown && !f.to && <span className="field-error">{t('ws.send.noEmail')}</span>}</label>
          <label className="field ws-full"><span className="field-label">{t('ws.send.cc')}</span><input value={f.cc} onChange={(x) => set('cc', x.target.value)} placeholder="a@exemple.com, b@exemple.com" /></label>
          <label className="field ws-full"><span className="field-label">{t('ws.send.subject')}</span><input value={f.subject} onChange={(x) => set('subject', x.target.value)} maxLength={250} /></label>
          <label className="field ws-full"><span className="field-label">{t('ws.send.message')}</span><textarea rows={7} value={f.message} onChange={(x) => set('message', x.target.value)} /></label>
          <p className="ws-hint ws-full">📎 {d.ref}.pdf — {t('ws.send.attached')}</p>
          <label className="field-checkbox ws-full"><input type="checkbox" checked={f.link} onChange={(x) => set('link', x.target.checked)} /><span>{t('ws.send.link')}</span></label>
        </div>
      )}
      <label className="field-checkbox"><input type="checkbox" checked={chat} onChange={(x) => setChat(x.target.checked)} /><span>{t('ws.send.chat')}{d.conversationId ? ` (${t('ws.send.chatOrigin')})` : ''}</span></label>
      <Alert>{error}</Alert>
      <div className="ws-actions">
        <button type="button" className="btn btn-ghost" onClick={onClose}>{t('common.cancel')}</button>
        <button type="button" className="btn btn-primary" disabled={busy || (!byEmail && !chat) || (byEmail && !f.to.trim())} onClick={send}>{busy ? t('ws.send.sending') : t('ws.docs.sendNow')}</button>
      </div>
    </Modal>
  );
}

// One document: preview, PDF, edit (draft), approval, send, duplicate, the customer's answer.
export function DocumentPage({ base = '/account/workspace' }) {
  const { id } = useParams();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const { t, lang, errorText } = useI18n();
  const [d, setD] = useState(null);
  const [error, setError] = useState('');
  const [msg, setMsg] = useState('');
  const [sending, setSending] = useState(false);
  const [pdf, setPdf] = useState(null);
  const [busy, setBusy] = useState('');
  const load = useCallback(() => api(`/workspace/documents/${id}`).then((x) => { setD(x); setError(''); }).catch((e) => setError(errorText(e))), [id, errorText]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (d && params.get('send') && d.canSend) { setSending(true); setParams({}, { replace: true }); } }, [d]); // eslint-disable-line react-hooks/exhaustive-deps
  async function act(path, body, done) {
    setBusy(path); setError('');
    try { await api(`/workspace/documents/${id}/${path}`, { method: 'POST', body: body || {} }); setMsg(done); load(); } catch (e) { setError(errorText(e)); } finally { setBusy(''); }
  }
  async function duplicate() {
    setBusy('dup');
    try { const n = await api(`/workspace/documents/${id}/duplicate`, { method: 'POST' }); navigate(`${base}/documents/${n.id}/edit`); } catch (e) { setError(errorText(e)); setBusy(''); }
  }
  async function remove() {
    if (!window.confirm(t('ws.docs.deleteConfirm'))) return;
    try { await api(`/workspace/documents/${id}`, { method: 'DELETE' }); navigate(`${base}/documents`); } catch (e) { setError(errorText(e)); }
  }
  async function showPdf() {
    setBusy('pdf');
    try { setPdf(await pdfBlobUrl(`/workspace/documents/${id}/pdf`)); } catch (e) { setError(errorText(e)); } finally { setBusy(''); }
  }
  if (!d) return <div className="ws-page"><DocsNav base={base} />{error ? <Alert>{error}</Alert> : <p className="muted">{t('common.loading')}</p>}</div>;
  // "Signé : Ali Khan", "Cachet ajouté : …" (other actions: their technical name).
  const histLabel = (h) => { const k = `sig.act.${h.action.replace(/\./g, '_')}`; const s = t(k, { details: h.details || '' }); return s === k ? `${h.action}${h.details ? ` — ${h.details}` : ''}` : s; };
  const at = STEPS.indexOf(['declined', 'expired', 'replied'].includes(d.status) ? 'viewed' : d.status);
  const needsApproval = d.approvalRequired && !d.approvedAt;
  return (
    <div className="ws-page">
      <DocsNav base={base} />
      <p className="small"><Link to={`${base}/documents`}>← {t('ws.docs.title')}</Link> · <Link to={`${base}/customers/${d.crmId}`}>{d.customer}</Link></p>
      <div className="acc-head"><TutorialSlot place="ws.docs.page" />
        <div><h1>{d.title}</h1><p className="muted">{d.ref}{d.version > 1 ? ` · v${d.version}` : ''} · {d.customer} · {t('ws.docs.byOn', { name: d.createdBy, date: fmtDate(d.createdAt, lang) })}</p></div>
        <div className="acc-head-actions">
          <StatusBadge kind="doc" value={d.status} />
          <button type="button" className="btn btn-ghost btn-sm" disabled={busy === 'pdf'} onClick={showPdf}>{t('ws.docs.viewPdf')}</button>
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => openPdf(d.id, errorText, setError)}>{t('ws.docs.download')}</button>
        </div>
      </div>
      <ol className="ws-steps">{STEPS.filter((s) => s !== 'pending_approval' || d.approvalRequired).map((s) => <li key={s} className={STEPS.indexOf(s) < at ? 'done' : STEPS.indexOf(s) === at ? 'now' : ''}>{labelOf(t, 'ws.doc', s)}</li>)}</ol>
      <Alert>{error}</Alert>
      {msg && <p className="callout callout-success" role="status">{msg}</p>}
      <div className="ws-cols">
        <section className="ws-card">
          {pdf ? <><PdfFrame url={pdf} title={d.ref} /><button type="button" className="btn btn-link btn-sm" onClick={() => setPdf(null)}>{t('ws.docs.backToText')}</button></> : <Preview html={d.html} items={d.items} lang={d.lang} />}
        </section>
        <aside className="ws-card">
          <h2>{t('ws.docs.actions')}</h2>
          {d.canEdit && <Link className="btn btn-ghost" to={`${base}/documents/${d.id}/edit`}>{t('ws.docs.edit')}</Link>}
          {d.status === 'draft' && needsApproval && <button type="button" className="btn btn-primary" disabled={!!busy} onClick={() => act('submit', null, t('ws.docs.submitted'))}>{t('ws.docs.submit')}</button>}
          {d.canApprove && (
            <div className="ws-row">
              <button type="button" className="btn btn-primary" disabled={!!busy} onClick={() => act('approve', { approve: true }, t('ws.docs.approved'))}>✓ {t('ws.docs.approve')}</button>
              <button type="button" className="btn btn-ghost" disabled={!!busy} onClick={() => { const c = window.prompt(t('ws.docs.rejectWhy')); if (c !== null) act('approve', { approve: false, comment: c }, t('ws.docs.rejected')); }}>{t('ws.docs.reject')}</button>
            </div>
          )}
          {d.approvedAt && <p className="small">✓ {t('ws.docs.approvedBy', { name: d.approvedBy, date: fmtDate(d.approvedAt, lang, true) })}</p>}
          {d.canSend && ['draft', 'sent', 'viewed', 'replied', 'expired'].includes(d.status) && !needsApproval && (
            <button type="button" className="btn btn-primary" onClick={() => setSending(true)}>✉ {d.status === 'draft' ? t('ws.docs.send') : t('ws.docs.resend')}</button>
          )}
          {d.status === 'draft' && needsApproval && !d.canApprove && <p className="ws-hint">{t('ws.docs.needsApproval')}</p>}
          {d.status === 'draft' && !needsApproval && !d.canSend && <p className="ws-hint">{t('ws.docs.managerSends')}</p>}
          {d.canDuplicate && <button type="button" className="btn btn-ghost" disabled={!!busy} onClick={duplicate}>{t('ws.docs.newVersion')}</button>}
          {d.status === 'draft' && !d.sentAt && d.canEdit && <button type="button" className="btn btn-link is-danger" onClick={remove}>{t('ws.docs.delete')}</button>}
          <dl className="ws-kv">
            {d.sentAt && <><dt>{t('ws.doc.sent')}</dt><dd>{fmtDate(d.sentAt, lang, true)} ({d.sentVia}){d.emailTo ? <><br /><span className="small muted">{d.emailTo}{d.emailCc ? ` · cc ${d.emailCc}` : ''}</span></> : null}</dd></>}
            {d.viewedAt && <><dt>{t('ws.doc.viewed')}</dt><dd>{fmtDate(d.viewedAt, lang, true)}</dd></>}
            {d.respondedAt && <><dt>{labelOf(t, 'ws.doc', d.status)}</dt><dd>{d.responseName} — {fmtDate(d.respondedAt, lang, true)}<br /><span className="muted small">IP {d.responseIp}</span></dd></>}
            {d.validUntil && <><dt>{t('ws.docf.validity')}</dt><dd>{fmtDate(d.validUntil, lang)}</dd></>}
            {d.totals?.priced && <><dt>{t('ws.dw.total')}</dt><dd>{money(d.totals.total, d.lang)} {d.items?.currency}</dd></>}
            {d.layout?.stamp && <><dt>{t('ws.lay.stamp')}</dt><dd>{d.layout.stamp.name}<br /><span className="small muted">{t('ws.lay.stampAdded', { name: d.layout.stamp.addedByName || '—', date: fmtDate(d.layout.stamp.addedAt, lang, true) })}</span></dd></>}
          </dl>
          {!['draft', 'pending_approval'].includes(d.status) && <p className="small"><span className="muted">{t('ws.docs.link')} :</span> <a href={d.link} target="_blank" rel="noreferrer">{d.link.replace(/^https?:\/\//, '')}</a></p>}
          {d.signatures?.length > 0 && (
            <>
              <h3>{t('sig.title2')}</h3>
              <ul className="ws-list sig-status">
                {d.signatures.map((s) => (
                  <li key={s.slot} className="small">
                    <span className="ws-grow"><strong>{s.name}</strong>{s.title ? ` — ${s.title}` : ''}<br />
                      <span className="muted">{s.status === 'signed' ? t('sig.signedBy', { name: s.signedBy || s.name, date: fmtDate(s.signedAt, lang, true), ip: s.ip || '—' }) : t('sig.pending')}</span></span>
                    {d.toSign?.includes(s.slot) && <button type="button" className="btn btn-primary btn-sm" disabled={!!busy} onClick={() => act('sign', { slot: s.slot }, t('sig.signedOk'))}>✍ {t('sig.sign')}</button>}
                  </li>
                ))}
              </ul>
            </>
          )}
          {d.history?.length > 0 && (
            <details className="doc-history">
              <summary>{t('sig.history')}</summary>
              <ul className="ws-list">{d.history.map((h, i) => <li key={i} className="small"><span className="ws-grow">{histLabel(h)}<br /><span className="muted">{h.by || '—'}{h.admin ? ` — ${t('ws.docs.preparedBy', { name: h.admin })}` : ''} — {fmtDate(h.at, lang, true)}</span></span></li>)}</ul>
            </details>
          )}
          {d.comments.length > 0 && (
            <>
              <h3>{t('ws.docs.comments')}</h3>
              <ul className="ws-list">{d.comments.map((c, i) => <li key={i} className="small"><span className="ws-grow"><strong>{c.name}</strong> — {c.body}</span><span className="muted">{fmtDate(c.at, lang)}</span></li>)}</ul>
            </>
          )}
          {d.replies?.length > 0 && (
            <>
              <h3>💬 {t('ws.docs.replies', { n: d.replies.length })}</h3>
              <ul className="ws-list">
                {d.replies.map((r, i) => (
                  <li key={i} className="small" style={{ display: 'block' }}>
                    <div className="ws-row"><strong className="ws-grow">{r.from}{r.subject ? ` — ${r.subject}` : ''}</strong><span className="muted">{fmtDate(r.at, lang, true)}</span></div>
                    <div className="ws-tl-mail" dangerouslySetInnerHTML={{ __html: r.body }} />
                  </li>
                ))}
              </ul>
            </>
          )}
        </aside>
      </div>
      {sending && <SendDialog d={d} onClose={() => setSending(false)} onSent={(r) => {
        setSending(false); setD(r);
        setMsg(r.via.includes('email') ? (r.sentVia && r.mailbox?.connected ? t('ws.send.sentOwn', { to: r.emailTo }) : t('ws.send.sentSite', { to: r.emailTo })) + (r.via.includes('chat') ? ` ${t('ws.send.alsoChat')}` : '') : t('ws.send.sentChat'));
        if (r.mailFailed) setError(errorText({ code: r.mailFailed }));
      }} />}
    </div>
  );
}
